import { api, setActivation } from 'services/api';
import { numberToTime } from 'utils/parseDateTime';
import { createRestProvider } from './restProvider';

const dataProvider = createRestProvider();

const TIME_FORMAT = 'HH:mm';

const timeStringToNumber = (time?: string | null) => {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 100 + (minutes || 0);
};

// Form field name (snake_case, as used by the pages) -> API field name
const PLAIN_FIELDS: Record<string, string> = {
  name: 'name',
  address: 'address',
  email: 'email',
  fax: 'fax',
  post_code: 'postCode',
  phone: 'phone',
  web: 'web',
  child_policy: 'childPolicy',
  cancelation_policy: 'cancelationPolicy',
  description: 'description',
  promotion_information: 'promotionInformation',
  is_recommended: 'isRecommended',
  is_active: 'isActivated'
};

dataProvider.getOne = async ({ id }) => {
  const { data: supplier } = await api.get<{ data: Record<string, any> }>(
    `/admin/suppliers/${id}`
  );

  const toTime = (time?: string | null) =>
    numberToTime(timeStringToNumber(time) as number);

  return {
    data: {
      id: supplier.id,
      code: supplier.code,
      name: supplier.name,
      address: supplier.address,
      email: supplier.email,
      fax: supplier.fax,
      post_code: supplier.postCode,
      phone: supplier.phone,
      web: supplier.web,
      checkin_time: toTime(supplier.checkinTime),
      checkout_time: toTime(supplier.checkoutTime),
      location: `${supplier.location?.latitude}, ${supplier.location?.longitude}`,
      child_policy: supplier.childPolicy,
      cancelation_policy: supplier.cancelationPolicy,
      description: supplier.description,
      promotion_information: supplier.promotionInformation,
      is_active: supplier.isActivated,
      is_recommended: supplier.isRecommended
    } as any
  };
};

dataProvider.update = async ({ variables, id }) => {
  const values: Record<string, any> = variables as any;
  const body: Record<string, unknown> = {};

  Object.entries(PLAIN_FIELDS).forEach(([key, apiKey]) => {
    if (values[key] !== undefined && values[key] !== null) {
      body[apiKey] = values[key];
    }
  });

  if ('checkin_time' in values) {
    body.checkinTime = values.checkin_time
      ? values.checkin_time.format(TIME_FORMAT)
      : '';
  }
  if ('checkout_time' in values) {
    body.checkoutTime = values.checkout_time
      ? values.checkout_time.format(TIME_FORMAT)
      : '';
  }

  if (typeof values.location === 'string') {
    const [latitude, longitude] = values.location
      .split(',')
      .map((part: string) => parseFloat(part));
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      body.location = { latitude, longitude };
    }
  }

  // Activation has its own endpoint
  const { isActivated, ...rest } = body;
  if (isActivated !== undefined) {
    if (Object.keys(rest).length === 0) {
      const { data } = await setActivation<{ data: any }>(
        'suppliers',
        id,
        Boolean(isActivated)
      );
      return { data };
    }
  }

  const { data } = await api.patch<{ data: any }>(
    `/admin/suppliers/${id}`,
    rest
  );
  if (isActivated !== undefined) {
    await setActivation('suppliers', id, Boolean(isActivated));
  }
  return { data };
};

export const hotelDataProvider = dataProvider;
